import React, { useState, useEffect, useRef } from 'react';
import NavBar from './NavBar'; // Assuming NavBar is in components
import { Box, Typography, List, ListItem, ListItemText, CircularProgress, IconButton, Paper, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TestGroupItem from './TestGroupItem'; // Assuming TestGroupItem is in components
import AddTestGroupModal from './AddTestGroupModal'; // Assuming AddTestGroupModal is in components
import { useSidebar } from '../contexts/SidebarContext';
import axios from 'axios';
import { supabase } from '../supabaseClient';
import { API_URL } from '../globals';

// TODO: Fetch studio details based on studioId if needed for the header
// TODO: Update fetchTestGroups to potentially filter by studioId if the API supports it

function TestRunsPage() {
  const { isOpen, isHovered } = useSidebar();
  const navbarWidth = isOpen || isHovered ? 240 : 64;

  const [testGroups, setTestGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [newGroupId, setNewGroupId] = useState(null);

  const fetchTestGroups = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const session = await supabase.auth.getSession();
      if (!session?.data?.session?.access_token) {
        throw new Error("Not authenticated");
      }
      const authToken = session.data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };

      const response = await axios.get(`${API_URL}/test_groups`, { headers });

      if (response.data && response.data.test_groups) {
        // TODO: Filter groups by studioId if the API returns all groups
        const relevantGroups = response.data.test_groups; // .filter(group => group.studio_id === studioId);
        
        const sortedGroups = [...relevantGroups].sort((a, b) => {
          const dateA = new Date(a.created_at?.$date || a.created_at || 0);
          const dateB = new Date(b.created_at?.$date || b.created_at || 0);
          return dateB - dateA; // newest first
        });
        setTestGroups(sortedGroups);
      } else {
        setTestGroups([]);
      }
    } catch (err) {
      console.error("Error fetching test groups:", err);
      setError(err.message || "Failed to fetch test groups");
      setTestGroups([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTestGroups();
  }, []); // Re-fetch if studioId changes

  useEffect(() => {
    // Remove highlight after 5 seconds
    if (newGroupId) {
      const timer = setTimeout(() => {
        setNewGroupId(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [newGroupId]);

  const handleOpenModal = () => setModalOpen(true);
  
  const handleCloseModal = (createdGroupId) => {
    setModalOpen(false);
    
    if (createdGroupId) {
      setNewGroupId(createdGroupId);
      fetchTestGroups(); // Refresh list
    }
  };

  return (
    <>
      {/* NavBar might need context or props if not using global state */}
      <NavBar /> 
      
      <Box
        component="main" 
        sx={{
          minHeight: '100vh', 
          marginLeft: `${navbarWidth}px`,
          transition: 'margin-left 0.2s',
          width: `calc(100% - ${navbarWidth}px)`,
          p: 3,
          backgroundColor: '#121212',
          boxSizing: 'border-box'
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            mb: 6
          }}
        >
          <Box>
             {/* TODO: Update Header to show Studio Name */}
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600, 
                color: '#fff',
                mb: 1
              }}
            >
              Test Runs
            </Typography>
            <Typography sx={{ color: '#888', fontSize: '0.875rem' }}>
              View and manage all test groups and their results
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
             {/* TODO: Pass studioId to AddTestGroupModal if needed */}
            <IconButton 
              onClick={handleOpenModal} 
              sx={{ 
                border: '1px solid #333',
                borderRadius: 1,
                p: 1,
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#252525',
                }
              }}
            >
              <AddIcon />
            </IconButton>
          </Box>
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography sx={{ color: 'error.main', textAlign: 'center' }}>
            Error: {error}
          </Typography>
        )}

        {!isLoading && !error && (
          <Paper 
            sx={{ 
              backgroundColor: '#1A1A1A', 
              p: 2, 
              borderRadius: 1,
              border: '1px solid rgba(255, 255, 255, 0.08)'
            }}
          >
            <List sx={{ p: 0 }}>
              {testGroups.length > 0 ? (
                testGroups.map((group) => (
                  <TestGroupItem 
                    key={group.group_id || group._id?.$oid || Math.random()} 
                    group={group} 
                    isNewGroup={group.group_id === newGroupId}
                  />
                ))
              ) : (
                <ListItem sx={{ borderRadius: 1 }}>
                  <ListItemText 
                    primary="No test groups found." 
                    sx={{ 
                      color: '#888', 
                      textAlign: 'center',
                      fontStyle: 'italic',
                      fontSize: '0.875rem'
                    }} 
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        )}
      </Box>

      <AddTestGroupModal 
        open={modalOpen}
        onClose={handleCloseModal}
        // Pass studioId if the modal needs it to create a group for a specific studio
        // studioId={studioId} 
      />
    </>
  );
}

export default TestRunsPage; 